import { useEffect, useRef, useState } from "react";

// Illustrative charts drawn from the published sample config. They restate the
// schedule; they are not forecasts and never show member results.

function useInView(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return seen;
}

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

// Eased 0→1 progress that starts once `active` turns true.
function useProgress(active, duration) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (prefersReducedMotion()) {
      setProgress(1);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, duration]);
  return progress;
}

export function CountUp({ value, suffix = "", duration = 1400 }) {
  const ref = useRef(null);
  const seen = useInView(ref);
  const shown = Math.round(value * useProgress(seen, duration));

  return (
    <span ref={ref} aria-label={`${value}${suffix}`}>
      {shown}
      {suffix}
    </span>
  );
}

const W = 600;

export function ScheduleChart({ pkg, postedDay, height = 260, compact = false }) {
  const ref = useRef(null);
  const seen = useInView(ref);
  const [hover, setHover] = useState(null);
  const progress = useProgress(seen, compact ? 1200 : 1800);

  const days = pkg?.durationDays;
  const daily = pkg?.dailyValue;
  if (!days || !daily) return <VariablePlaceholder compact={compact} height={height} />;

  const total = days * daily;
  const pad = compact ? { l: 4, r: 4, t: 10, b: 6 } : { l: 44, r: 18, t: 18, b: 32 };
  const x = (d) => pad.l + (d / days) * (W - pad.l - pad.r);
  const y = (v) => height - pad.b - (v / total) * (height - pad.t - pad.b);
  const split = postedDay ?? days;
  const base = y(0);
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const gid = `g-${pkg.id}-${compact ? "c" : "f"}`;

  function onMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const day = Math.round(((px - pad.l) / (W - pad.l - pad.r)) * days);
    setHover(Math.max(0, Math.min(days, day)));
  }

  return (
    <div ref={ref} className={`chart ${compact ? "chart-compact" : ""}`}>
      <svg
        viewBox={`0 0 ${W} ${height}`}
        role="img"
        aria-label={`Illustrative schedule for ${pkg.name}: ${daily} ${pkg.currency} per day for ${days} days, ${total} ${pkg.currency} scheduled in total.`}
        onPointerMove={compact ? undefined : onMove}
        onPointerLeave={compact ? undefined : () => setHover(null)}
      >
        <defs>
          <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
          </linearGradient>
          <clipPath id={`${gid}-clip`}>
            <rect x="-20" y="-20" width={(W + 40) * progress} height={height + 40} />
          </clipPath>
        </defs>

        {!compact &&
          ticks.map((t) => (
            <g key={t} className="grid">
              <line x1={pad.l} x2={W - pad.r} y1={y(total * t)} y2={y(total * t)} />
              <text x={pad.l - 10} y={y(total * t) + 4} textAnchor="end">
                {Math.round(total * t)}
              </text>
              <text x={x(days * t)} y={height - 10} textAnchor="middle">
                {t === 0 ? "Day 0" : Math.round(days * t)}
              </text>
            </g>
          ))}

        <g className="plot" clipPath={`url(#${gid}-clip)`}>
          <path
            d={`M${x(0)},${base} L${x(split)},${y(split * daily)} L${x(split)},${base} Z`}
            fill={`url(#${gid})`}
          />
          <line className="line-posted" x1={x(0)} y1={base} x2={x(split)} y2={y(split * daily)} />
          {split < days && (
            <line className="line-scheduled" x1={x(split)} y1={y(split * daily)} x2={x(days)} y2={y(total)} />
          )}
          {postedDay != null && (
            <g className="marker">
              <circle className="pulse" cx={x(split)} cy={y(split * daily)} r="10" />
              <circle cx={x(split)} cy={y(split * daily)} r="5" />
            </g>
          )}
        </g>

        {hover != null && (
          <g className="crosshair">
            <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={base} />
            <circle cx={x(hover)} cy={y(hover * daily)} r="5" />
          </g>
        )}
      </svg>

      {hover != null && (
        <div className="tooltip" style={{ left: `${(x(hover) / W) * 100}%` }}>
          <strong>Day {hover}</strong>
          <span>
            {hover * daily} {pkg.currency} cumulative
          </span>
          {postedDay != null && <em>{hover <= postedDay ? "Posted" : "Scheduled"}</em>}
        </div>
      )}
    </div>
  );
}

function VariablePlaceholder({ compact, height }) {
  const h = compact ? height : 200;
  const points = Array.from({ length: 13 }, (_, i) => `${(i / 12) * W},${h / 2 + Math.sin(i * 0.9) * (h * 0.12)}`);
  return (
    <div className={`chart chart-variable ${compact ? "chart-compact" : ""}`}>
      <svg viewBox={`0 0 ${W} ${h}`} aria-hidden="true">
        <polyline points={points.join(" ")} />
      </svg>
      <p>Formula-based · no fixed total</p>
    </div>
  );
}

export function LevelBars({ levels }) {
  const ref = useRef(null);
  const seen = useInView(ref);
  const [hover, setHover] = useState(null);
  const rates = levels.map((item) => parseFloat(item.rate) || 0);
  const max = Math.max(...rates, 1);

  return (
    <div ref={ref} className={`bars ${seen ? "is-drawn" : ""}`}>
      {levels.map((item, index) => (
        <div
          key={item.level}
          className={`bar-col ${hover === index ? "is-hover" : ""}`}
          onPointerEnter={() => setHover(index)}
          onPointerLeave={() => setHover(null)}
        >
          <span className="bar-value">{item.rate}</span>
          <div className="bar-track">
            <div
              className="bar"
              style={{ height: `${(rates[index] / max) * 100}%`, transitionDelay: `${index * 90}ms` }}
            />
          </div>
          <span className="bar-label">L{item.level}</span>
          {hover === index && (
            <div className="tooltip bar-tip">
              <strong>Level {item.level}</strong>
              <span>{item.rate} rate</span>
              <span>Needs {item.directs}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
